import React, { useEffect, useState } from 'react';
import {
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  Space,
  Row,
  Col,
  Radio,
  Tooltip,
  Modal,
} from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { generatePlaceholderEmail, generateRandomPassword } from '../../utils/generateCredentials';
import { PASSWORD_MIN_LENGTH, NO_EDGE_WHITESPACE_REGEX } from '../../utils/passwordValidation';
import { useAuth } from '../../contexts/AuthContext';

const { Option } = Select;

const ADMIN_ROLE = 'ADMIN';
// Listed most→least privileged. ADMIN is offered only when an admin actor creates a
// new account; an existing admin target is shown locked (see below), and ADMIN is
// never offered when editing a non-admin — promotion is create-only on the backend.
const SELECTABLE_ROLES = ['HR', 'RECEPTION', 'EMPLOYEE'];

const ROLE_TOOLTIP_KEYS = {
  ADMIN: 'employee.form.roleAdminTooltip',
  HR: 'employee.form.roleHrTooltip',
  EMPLOYEE: 'employee.form.roleEmployeeTooltip',
  RECEPTION: 'employee.form.roleReceptionTooltip',
};

const TYPE_EMPLOYEE = 'EMPLOYEE';
const TYPE_COLLABORATOR = 'COLLABORATOR';

const EmployeeForm = ({ onSubmit, employee = null, loading = false, departments = [] }) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const { canManageEmployees, isAdmin } = useAuth();
  // Pending employment-type switch awaiting confirmation; null when no dialog is open.
  const [pendingTypeSwitch, setPendingTypeSwitch] = useState(null);

  const canEditRole = canManageEmployees();
  const isAdminTarget = employee?.role === ADMIN_ROLE;
  // ADMIN is selectable only when an admin actor creates a brand-new account.
  const roleOptions = !employee && isAdmin() ? [ADMIN_ROLE, ...SELECTABLE_ROLES] : SELECTABLE_ROLES;

  useEffect(() => {
    if (employee) {
      form.setFieldsValue({
        ...employee,
        hireDate: employee.hireDate ? dayjs(employee.hireDate) : null,
        password: '',
      });
    } else {
      form.resetFields();
      // Login credentials are not relevant at creation time, so prefill a valid
      // placeholder email and a random password. The admin can change them now
      // or later (see the hints under each field).
      form.setFieldsValue({
        email: generatePlaceholderEmail(),
        password: generateRandomPassword(),
      });
    }
  }, [employee, form]);

  const infoIcon = (titleKey) => (
    <Tooltip title={t(titleKey)}>
      <InfoCircleOutlined
        onClick={(e) => e.stopPropagation()}
        style={{ marginLeft: 6, opacity: 0.65 }}
      />
    </Tooltip>
  );

  const submit = (formData) => {
    if (employee && !formData.password) {
      delete formData.password;
    }
    onSubmit(formData);
  };

  const handleFinish = (values) => {
    const formData = {
      ...values,
      hireDate: values.hireDate ? values.hireDate.format('YYYY-MM-DD') : null,
    };

    // Switching the employment type has leave-balance side effects, so confirm it
    // explicitly before submitting (allocate quota for EMPLOYEE, drop it for COLLABORATOR).
    const typeChanged = employee && employee.employeeType && values.employeeType !== employee.employeeType;
    if (typeChanged) {
      setPendingTypeSwitch({
        toEmployee: values.employeeType === TYPE_EMPLOYEE,
        formData,
        hireDate: values.hireDate || dayjs(),
      });
      return;
    }

    submit(formData);
  };

  const confirmTypeSwitch = () => {
    if (!pendingTypeSwitch) {
      return;
    }
    let { formData } = pendingTypeSwitch;
    if (pendingTypeSwitch.toEmployee && pendingTypeSwitch.hireDate) {
      const confirmedHireDate = pendingTypeSwitch.hireDate;
      formData = { ...formData, hireDate: confirmedHireDate.format('YYYY-MM-DD') };
      form.setFieldValue('hireDate', confirmedHireDate);
    }
    setPendingTypeSwitch(null);
    submit(formData);
  };

  return (
    <>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        initialValues={{
          employeeType: TYPE_EMPLOYEE,
          role: 'EMPLOYEE',
          hireDate: dayjs(),
        }}
      >
        <Row gutter={[16, 0]}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="username"
              label={t('employee.form.username')}
              rules={[
                { required: true, message: t('employee.form.usernameRequired') },
                { min: 3, message: t('employee.form.usernameMinLength') },
              ]}
            >
              <Input placeholder={t('employee.form.username')} disabled={loading} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="email"
              label={t('employee.form.email')}
              extra={employee ? undefined : t('employee.form.emailAutoHint')}
              rules={[
                { required: true, message: t('employee.form.emailRequired') },
                { type: 'email', message: t('employee.form.emailInvalid') },
              ]}
            >
              <Input placeholder={t('employee.form.email')} disabled={loading} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="password"
          label={employee ? t('employee.form.passwordKeepEmpty') : t('employee.form.password')}
          extra={employee ? undefined : t('employee.form.passwordAutoHint')}
          rules={[
            ...(employee ? [] : [{ required: true, message: t('employee.form.passwordRequired') }]),
            { min: PASSWORD_MIN_LENGTH, message: t('employee.form.passwordMinLength') },
            { pattern: NO_EDGE_WHITESPACE_REGEX, message: t('employee.form.passwordNoEdgeWhitespace') },
          ]}
        >
          <Input.Password
            placeholder={t('employee.form.password')}
            autoComplete="new-password"
            disabled={loading}
          />
        </Form.Item>

        <Row gutter={[16, 0]}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="firstName"
              label={t('employee.form.firstName')}
              rules={[{ required: true, message: t('employee.form.firstNameRequired') }]}
            >
              <Input placeholder={t('employee.form.firstName')} disabled={loading} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="lastName"
              label={t('employee.form.lastName')}
              rules={[{ required: true, message: t('employee.form.lastNameRequired') }]}
            >
              <Input placeholder={t('employee.form.lastName')} disabled={loading} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[16, 0]}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="position"
              label={t('employee.form.position')}
              rules={[{ required: true, message: t('employee.form.positionRequired') }]}
            >
              <Input placeholder={t('employee.form.position')} disabled={loading} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="departmentId"
              label={t('employee.form.department')}
              rules={[{ required: true, message: t('employee.form.departmentRequired') }]}
            >
              <Select
                placeholder={t('employee.form.departmentPlaceholder')}
                disabled={loading}
                showSearch
                optionFilterProp="label"
              >
                {departments.map((dept) => (
                  <Option key={dept.id} value={dept.id} label={dept.name}>
                    <Space>
                      <div
                        style={{
                          width: 12,
                          height: 12,
                          backgroundColor: dept.colorHex,
                          borderRadius: '50%',
                          display: 'inline-block',
                        }}
                      />
                      {dept.name}
                    </Space>
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="employeeType"
          label={t('employee.form.employeeType')}
          rules={[{ required: true, message: t('employee.form.employeeTypeRequired') }]}
        >
          <Radio.Group disabled={loading}>
            <Radio.Button value={TYPE_EMPLOYEE}>
              {t('employee.form.employeeTypeEmployee')}
              {infoIcon('employee.form.typeEmployeeTooltip')}
            </Radio.Button>
            <Radio.Button value={TYPE_COLLABORATOR}>
              {t('employee.form.employeeTypeCollaborator')}
              {infoIcon('employee.form.typeCollaboratorTooltip')}
            </Radio.Button>
          </Radio.Group>
        </Form.Item>

        {canEditRole && (
          isAdminTarget ? (
            <Form.Item name="role" label={t('employee.form.accountRole')}>
              <Radio.Group disabled>
                <Radio.Button value={ADMIN_ROLE}>
                  {t('dashboard.roles.ADMIN')}
                  {infoIcon('employee.form.roleLockedAdminTooltip')}
                </Radio.Button>
              </Radio.Group>
            </Form.Item>
          ) : (
            <Form.Item
              name="role"
              label={t('employee.form.accountRole')}
              rules={[{ required: true, message: t('employee.form.accountRoleRequired') }]}
            >
              <Radio.Group disabled={loading}>
                {roleOptions.map((role) => (
                  <Radio.Button key={role} value={role}>
                    {t(`dashboard.roles.${role}`)}
                    {infoIcon(ROLE_TOOLTIP_KEYS[role])}
                  </Radio.Button>
                ))}
              </Radio.Group>
            </Form.Item>
          )
        )}

        <Row gutter={[16, 0]}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="hireDate"
              label={t('employee.form.hireDate')}
              rules={[{ required: true, message: t('employee.form.hireDateRequired') }]}
            >
              <DatePicker
                style={{ width: '100%' }}
                disabled={loading}
                format="YYYY-MM-DD"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="phone"
              label={t('employee.form.phone')}
              rules={[
                {
                  pattern: /^[\d\s\-+()]+$/,
                  message: t('employee.form.phoneInvalid')
                },
              ]}
            >
              <Input placeholder={t('employee.form.phonePlaceholder')} disabled={loading} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="cnp"
          label={t('employee.form.cnp')}
          rules={[
            {
              pattern: /^\d{13}$/,
              message: t('employee.form.cnpInvalid'),
            },
          ]}
        >
          <Input placeholder={t('employee.form.cnpPlaceholder')} disabled={loading} maxLength={13} />
        </Form.Item>

        <Form.Item
          name="address"
          label={t('employee.form.address')}
        >
          <Input.TextArea
            placeholder={t('employee.form.addressPlaceholder')}
            disabled={loading}
            rows={3}
          />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={loading}>
              {employee ? t('common.update') : t('common.create')}
            </Button>
          </Space>
        </Form.Item>
      </Form>

      <Modal
        open={!!pendingTypeSwitch}
        title={
          pendingTypeSwitch?.toEmployee
            ? t('employee.form.typeSwitchToEmployeeTitle')
            : t('employee.form.typeSwitchToCollaboratorTitle')
        }
        okText={t('common.confirm')}
        cancelText={t('common.cancel')}
        onOk={confirmTypeSwitch}
        onCancel={() => setPendingTypeSwitch(null)}
        confirmLoading={loading}
        destroyOnClose
      >
        {pendingTypeSwitch?.toEmployee ? (
          <>
            <p>{t('employee.form.typeSwitchToEmployeeBody', { year: dayjs().year() })}</p>
            <Form.Item label={t('employee.form.confirmHireDateLabel')} style={{ marginBottom: 0 }}>
              <DatePicker
                style={{ width: '100%' }}
                format="YYYY-MM-DD"
                value={pendingTypeSwitch?.hireDate}
                onChange={(date) => setPendingTypeSwitch((prev) => ({ ...prev, hireDate: date }))}
              />
            </Form.Item>
          </>
        ) : (
          <p>{t('employee.form.typeSwitchToCollaboratorBody')}</p>
        )}
      </Modal>
    </>
  );
};

export default EmployeeForm;
